import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Text } from 'rizzui';
import { ActivityChangesModalContent, renderActivityChangesSummary, renderActivityIpAddress } from '../../../../../components/activity/ActivityAuditDetail';
import AsyncImg from '../../../../../components/extras/AsyncImg';
import ModalForm from '../../../../../components/forms/ModalForm';
import { useFiltersPR } from '../../../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import CustomTable from '../../../../../components/table/CustomTable';
import AvatarCard from '../../../../../components/ui/AvatarCard';
import useFetch from '../../../../../hooks/useFetch';
import Page from '../../../../../layout/Page/Page';
import { UserService } from '../../../../../services/user/userService';
import { Changelog } from '../../../../../type/entities/changelog-type';
import { UserApiResponse } from '../../../../../type/entities/user-type';
import UserActivityHistoryFilters from './UserActivityHistoryFilters';

interface UserActivityHistoryProps { }

const columnOrder = [
    'id',
    'user',
    'entity',
    'company',
    'description',
    'actionType',
    'changes',
    'ipAddress',
    'entityType',
    'createdAt',
    'date',
];

const UserActivityHistory: React.FC<UserActivityHistoryProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [selectedChanges, setSelectedChanges] = useState<Changelog['changes']>(null);

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new UserService()).listChangelogs(id, filters);
        return response.getResponseData() as UserApiResponse;
    }, [id, filters]));

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = { ...row };
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <Page container='fluid' className='mt-5'>
            {data && (
                <CustomTable
                    id={'ActivityHistory-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    columnsNotShown={['id', 'entity', 'relationModifiedId', 'updatedAt', 'createdAt']}
                    overrideColumns={[
                        {
                            key: 'user',
                            render: (row: any) => {
                                const entity = row.row.original?.user;

                                if (!entity) {
                                    return <Text>N/A</Text>
                                }

                                if (!Object.prototype.hasOwnProperty.call(entity, 'profileImg')) {
                                    return (
                                        <AvatarCard src={'default-avatar.png'} name={entity.name + (entity.lastName ? ` ${entity.lastName}` : '')} />
                                    )
                                }

                                return (
                                    <Link to={`/users/${entity.id}/edit`}>
                                        <div className="flex items-center gap-3">
                                            <AsyncImg id={entity.id} className="mx-auto d-block rounded w-[40px] h-[40px] object-cover" />
                                            <Text>{entity.name + (entity.lastName ? ` ${entity.lastName}` : '')}</Text>
                                        </div>
                                    </Link>
                                )
                            },
                        },
                        {
                            key: 'description',
                            columnNameKey: 'Comentario',
                            render: (row: any) => { return row.row.original.description },
                        },
                        {
                            key: 'actionType',
                            columnNameKey: 'Acción',
                            render: (row: any) => <Text>{row.row.original.actionType || 'Registro legado'}</Text>,
                        },
                        {
                            key: 'changes',
                            columnNameKey: 'Cambios',
                            render: (row: any) => renderActivityChangesSummary(
                                row.row.original.changes,
                                (changes) => setSelectedChanges(changes)
                            ),
                        },
                        {
                            key: 'ipAddress',
                            columnNameKey: 'IP',
                            render: (row: any) => renderActivityIpAddress(row.row.original),
                        },
                        {
                            key: 'entityType',
                            columnNameKey: 'Entidad',
                            render: (row: any) => { return row.row.original.entityType },
                        },
                        {
                            key: 'company',
                            render: (row: any) => { return row.row.original.company?.name },
                        },
                        {
                            key: 'date',
                            render: (row: any) => { return moment(row.row.original.createdAt?.date).format('DD/MM/YYYY H:mm') },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}
            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Actividad'}>
                <UserActivityHistoryFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
            <ModalForm
                isOpen={selectedChanges !== null}
                onClose={() => setSelectedChanges(null)}
                title='Detalle de cambios'
                size='xl'
            >
                <ActivityChangesModalContent changes={selectedChanges} />
            </ModalForm>
        </Page>
    );
};

export default UserActivityHistory;
